// The format bar (the Mac's FormatBar): only the prose dialect — paragraph style, bold, italic, underline, small
// capitals, alignment, text colour and highlight (the eight inks), line spacing, lists, block quote, scene break and a
// numbered footnote, and a link (Add Link… / Edit Link…, ⌥⌘L). Its state is the editor's `format()` at the caret. As it narrows it folds, as the Mac's does:
// alignment into a menu first, then colours and spacing into one. The keys (⌘B, ⌘I, ⌘U…) are the editor's own.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Alignment, InkColor, ListStyle } from '@writing-suite/core';
import { commands, type BlockStyle, type ProseFormat } from '@writing-suite/editor';
import type { StateCommand } from '@codemirror/state';
import { MenuButton, type MenuEntry } from './Menu.tsx';
import { Icon } from './Icons.tsx';

export const INKS: { ink: InkColor; name: string }[] = [
  { ink: 'yellow', name: 'Yellow' },
  { ink: 'green', name: 'Green' },
  { ink: 'blue', name: 'Blue' },
  { ink: 'pink', name: 'Pink' },
  { ink: 'purple', name: 'Purple' },
  { ink: 'orange', name: 'Orange' },
  { ink: 'red', name: 'Red' },
  { ink: 'grey', name: 'Grey' },
];

const STYLES: { style: BlockStyle; name: string }[] = [
  { style: 'body', name: 'Body' },
  { style: 'blockQuote', name: 'Block quote' },
  { style: 'epigraph', name: 'Epigraph' },
  { style: 'verse', name: 'Verse' },
];

const ALIGNMENTS: { alignment: Alignment | null; name: string; glyph: string }[] = [
  { alignment: null, name: 'Align left', glyph: '⯇' },
  { alignment: 'center', name: 'Centre', glyph: '≡' },
  { alignment: 'right', name: 'Align right', glyph: '⯈' },
  { alignment: 'justify', name: 'Justify', glyph: '☰' },
];

const LISTS: { style: ListStyle; name: string; glyph: string }[] = [
  { style: 'bullet', name: 'Bullets', glyph: '•' },
  { style: 'circle', name: 'Circles', glyph: '◦' },
  { style: 'dash', name: 'Dashes', glyph: '–' },
  { style: 'decimal', name: 'Numbered', glyph: '1.' },
  { style: 'decimalParen', name: 'Numbered with brackets', glyph: '1)' },
  { style: 'lowerAlpha', name: 'Lettered', glyph: 'a.' },
  { style: 'upperAlpha', name: 'Capital letters', glyph: 'A.' },
  { style: 'lowerRoman', name: 'Roman numerals', glyph: 'i.' },
  { style: 'upperRoman', name: 'Capital Roman numerals', glyph: 'I.' },
];

/** `LineSpacing`: a view setting (never stored in the project), as multiples of the type size. */
export const LINE_SPACINGS: { id: string; name: string; value: number }[] = [
  { id: 'single', name: 'Single', value: 1.25 },
  { id: '1.15', name: '1.15', value: 1.44 },
  { id: 'book', name: 'Book', value: 1.68 },
  { id: '1.5', name: '1.5', value: 1.88 },
  { id: 'double', name: 'Double', value: 2.5 },
];

type Density = 'full' | 'compact' | 'tight';

export function FormatBar({
  format,
  run,
  disabled,
  lineSpacing,
  setLineSpacing,
  onLink,
  children,
}: {
  format: ProseFormat | null;
  /** Runs a command in the editor, then the caret goes back to it. */
  run: (command: StateCommand) => void;
  disabled: boolean;
  lineSpacing: string;
  setLineSpacing: (id: string) => void;
  /** Add Link… or Edit Link… (the page asks for the address). */
  onLink?: () => void;
  /** Buttons after the bar's own (composition). */
  children?: ReactNode;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const [density, setDensity] = useState<Density>('full');
  useEffect(() => {
    const el = bar.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setDensity(w >= 760 ? 'full' : w >= 560 ? 'compact' : 'tight');
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const off = disabled || !format;
  const f = format ?? { style: 'body' as BlockStyle, alignment: null, list: null, inline: { italic: false, bold: false, smallCaps: false, underline: false, highlight: null, color: null, link: null }, link: null };
  // Alignment is for body paragraphs and verse: lists, quotations and epigraphs keep their own.
  const alignable = (f.style === 'body' || f.style === 'verse') && f.list === null;
  const styleName = STYLES.find((s) => s.style === f.style)?.name ?? 'Body';

  const inkEntries = (kind: 'color' | 'highlight'): MenuEntry[] => {
    const current = kind === 'color' ? f.inline.color : f.inline.highlight;
    const set = kind === 'color' ? commands.setTextColor : commands.setHighlight;
    return [
      { kind: 'radio', label: kind === 'color' ? 'Ink' : 'No highlight', checked: current === null, color: null, onSelect: () => run(set(null)) },
      ...INKS.map((i): MenuEntry => ({
        kind: 'radio', label: i.name, checked: current === i.ink, color: `var(--${kind === 'color' ? 'ink' : 'hl'}-${i.ink})`, onSelect: () => run(set(i.ink)),
      })),
    ];
  };
  const spacingEntries = (): MenuEntry[] =>
    LINE_SPACINGS.map((s) => ({ kind: 'radio', label: s.name, checked: s.id === lineSpacing, onSelect: () => setLineSpacing(s.id) }));
  const alignmentEntries = (): MenuEntry[] =>
    ALIGNMENTS.map((a) => ({ kind: 'radio', label: a.name, glyph: a.glyph, checked: alignable && f.alignment === a.alignment, onSelect: () => run(commands.setAlignment(a.alignment)) }));

  return (
    <div className="format-bar" role="toolbar" aria-label="Format" ref={bar} data-density={density}>
      <MenuButton
        label={`Paragraph style: ${styleName}`}
        className="format-menu style-menu"
        title="Paragraph style"
        menuLabel="Paragraph style"
        disabled={off}
        entries={() => STYLES.map((s) => ({ kind: 'radio', label: s.name, checked: f.style === s.style, onSelect: () => run(commands.setBlockStyle(s.style)) }))}
      >
        <span>{styleName}</span>
        <span className="option-chevron" aria-hidden="true" />
      </MenuButton>
      <Rule />
      <Toggle label="Bold" shortcut="⌘B" active={f.inline.bold} disabled={off} onPress={() => run(commands.toggleBold)}>
        <b>B</b>
      </Toggle>
      <Toggle label="Italic" shortcut="⌘I" active={f.inline.italic} disabled={off} onPress={() => run(commands.toggleItalic)}>
        <i>I</i>
      </Toggle>
      <Toggle label="Underline" shortcut="⌘U" active={f.inline.underline} disabled={off} onPress={() => run(commands.toggleUnderline)}>
        <u>U</u>
      </Toggle>
      <Toggle label="Small capitals" active={f.inline.smallCaps} disabled={off} onPress={() => run(commands.toggleSmallCaps)}>
        <span className="small-caps">Aa</span>
      </Toggle>
      {onLink ? (
        <Toggle label={f.link ? 'Edit Link…' : 'Add Link…'} shortcut="⌥⌘L" active={!!f.link} disabled={off} onPress={onLink}>
          <span className="link-glyph"><Icon name="link" size={16} /></span>
        </Toggle>
      ) : null}
      <Rule />
      {density === 'full' ? (
        ALIGNMENTS.map((a) => (
          <Toggle key={a.name} label={a.name} active={alignable && f.alignment === a.alignment} disabled={off || !alignable} onPress={() => run(commands.setAlignment(a.alignment))}>
            <AlignGlyph alignment={a.alignment} />
          </Toggle>
        ))
      ) : (
        <MenuButton label="Alignment" className="format-menu" disabled={off || !alignable} entries={alignmentEntries}>
          <AlignGlyph alignment={alignable ? f.alignment : null} />
          <span className="option-chevron" aria-hidden="true" />
        </MenuButton>
      )}
      <Rule />
      {density === 'tight' ? (
        <MenuButton
          label="Colour and spacing"
          className="format-menu"
          disabled={off}
          entries={() => [
            { kind: 'heading', label: 'Text colour' },
            ...inkEntries('color'),
            { kind: 'separator' },
            { kind: 'heading', label: 'Highlight' },
            ...inkEntries('highlight'),
            { kind: 'separator' },
            { kind: 'heading', label: 'Line spacing' },
            ...spacingEntries(),
          ]}
        >
          <span className="ink-glyph" aria-hidden="true">A</span>
          <span className="option-chevron" aria-hidden="true" />
        </MenuButton>
      ) : (
        <>
          <MenuButton label="Text colour" className="format-menu" disabled={off} entries={() => inkEntries('color')}>
            <span className={`ink-glyph${f.inline.color ? ` c-${f.inline.color}` : ''}`} aria-hidden="true">A</span>
          </MenuButton>
          <MenuButton label="Highlight" className="format-menu" disabled={off} entries={() => inkEntries('highlight')}>
            <span className={`hl-glyph${f.inline.highlight ? ` hl-${f.inline.highlight}` : ''}`} aria-hidden="true">ab</span>
          </MenuButton>
          <MenuButton label="Line spacing" className="format-menu" disabled={disabled} entries={spacingEntries}>
            <span aria-hidden="true">↕</span>
          </MenuButton>
        </>
      )}
      <MenuButton
        label="Lists"
        className="format-menu"
        disabled={off}
        entries={() => [
          { kind: 'radio', label: 'None', checked: f.list === null, onSelect: () => f.list && run(commands.setList(f.list)) },
          ...LISTS.map((l): MenuEntry => ({ kind: 'radio', label: l.name, glyph: l.glyph, checked: f.list === l.style, onSelect: () => run(commands.setList(l.style)) })),
        ]}
      >
        <span aria-hidden="true">{LISTS.find((l) => l.style === f.list)?.glyph ?? '•'}≡</span>
      </MenuButton>
      <Rule />
      <Toggle label="Block quote" active={f.style === 'blockQuote'} disabled={off} onPress={() => run(commands.setBlockStyle(f.style === 'blockQuote' ? 'body' : 'blockQuote'))}>
        <span className="quote-glyph">“</span>
      </Toggle>
      <Toggle label="Scene break" disabled={off} onPress={() => run(commands.insertSceneBreak)}>
        <span className="ornament-glyph">✦</span>
      </Toggle>
      <Toggle label="Footnote" disabled={off} onPress={() => run(commands.insertFootnote())} wide>
        <span className="note-glyph">
          Note<sup>1</sup>
        </span>
      </Toggle>
      {children ? <span className="format-extra">{children}</span> : null}
    </div>
  );
}

function Rule() {
  return <span className="format-rule" aria-hidden="true" />;
}

function Toggle({
  label,
  shortcut,
  active,
  disabled,
  onPress,
  children,
  wide,
}: {
  label: string;
  shortcut?: string;
  active?: boolean;
  disabled?: boolean;
  onPress: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <button
      type="button"
      className={`format-button${wide ? ' wide' : ''}`}
      aria-label={label}
      aria-pressed={active === undefined ? undefined : active}
      title={shortcut ? `${label} (${shortcut})` : label}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPress}
    >
      {children}
    </button>
  );
}

function AlignGlyph({ alignment }: { alignment: Alignment | null }) {
  return <span className={`align-glyph ${alignment ?? 'left'}`} aria-hidden="true"><i /><i /><i /></span>;
}
